import { unstable_batchedUpdates } from 'react-dom';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { NO_WORK, NewWork, Work, mergeWork, stripWork } from '../types';
import { logWork } from '../utils';
import {
  areSpiralReviewSquaresLocked,
  getReplacementTargetedId,
} from '../utils/spiralReviewLock';

import {
  NormalProblem,
  problemState,
  setProblem,
  setProblems,
} from './problemStore';

interface WorkStoreState {
  requireStudentsToFinishDefaultAssignment: boolean;
  requireStudentsToFinishAssignedSkills: boolean;
  isClasDefaultWorkSpiralReview: boolean;

  currentWork: Work;
}

export interface WorkStore extends WorkStoreState {
  setRequireStudentsToFinishDefaultAssignment: (
    requireStudentsToFinishDefaultAssignment: boolean
  ) => void;
  setRequireStudentsToFinishAssignedSkills: (
    requireStudentsToFinishAssignedSkills: boolean
  ) => void;
  setIsClasDefaultWorkSpiralReview: (
    isClasDefaultWorkSpiralReview: boolean
  ) => void;

  setCurrentWork: (work: Work) => void;
  updateCurrentWork: (work: Work) => void;
}

(window as any).__REDUX_DEVTOOLS_EXTENSION__;

export const workStore = create<WorkStore>()(
  devtools(
    immer((set, get) => ({
      isClasDefaultWorkSpiralReview: false,
      currentWork: NO_WORK,
      requireStudentsToFinishDefaultAssignment: false,
      requireStudentsToFinishAssignedSkills: false,
      setRequireStudentsToFinishDefaultAssignment: (
        requireStudentsToFinishDefaultAssignment: boolean
      ) => set({ requireStudentsToFinishDefaultAssignment }),
      setRequireStudentsToFinishAssignedSkills: (
        requireStudentsToFinishAssignedSkills: boolean
      ) => {
        set({ requireStudentsToFinishAssignedSkills });
        reselectIfSpiralReviewLocked();
      },
      setIsClasDefaultWorkSpiralReview: (
        isClasDefaultWorkSpiralReview: boolean
      ) => set({ isClasDefaultWorkSpiralReview }),
      setCurrentWork: (work: Work) => {
        if (work.type === 'NONE') problemState().clear();
        set({ currentWork: stripWork(work) });
        logWork('workStore.setCurrentWork (no problem data involved)');
      },
      updateCurrentWork: (update: Work) => {
        const currentWork = get().currentWork;

        set({ currentWork: mergeWork(currentWork, update) });
        logWork('workStore.updateCurrentWork (no problem data involved)');
        reselectIfSpiralReviewLocked();
      },
    })),
    { name: 'Work Store' }
  )
);

export const workState = (): WorkStore => workStore.getState();

function reselectIfSpiralReviewLocked(): void {
  const { requireStudentsToFinishAssignedSkills, currentWork } = workState();

  if (
    !areSpiralReviewSquaresLocked(
      requireStudentsToFinishAssignedSkills,
      currentWork
    )
  ) {
    return;
  }

  const { problems, selectedID, dollars } = problemState();
  const selected = problems[selectedID] as NormalProblem | undefined;

  if (!selected || selected.type !== 'SPIRAL_REVIEW') return;

  const replacementId = getReplacementTargetedId(problems, dollars);

  if (replacementId) setProblem(replacementId);
}

export function newWork(data?: NewWork): void {
  if (!data) return;

  unstable_batchedUpdates(() => {
    workState().setCurrentWork(data as Work);
    setProblems(data);
    reselectIfSpiralReviewLocked();
  });
}
